import React from 'react';
import HomeLink from './HomeLink';
import RowHeader from './RowHeader';
import { getAreaTileSrcSet, getAreaTileUrl } from '../../../utils/imageUrl';
import { formatCount } from './HeroSearch';
import { CARD_HOVER, FOCUS_RING } from './tokens';

/**
 * HOME-01 — "Browse by area".
 *
 * COUNTS COME FROM ONE AGGREGATE, and that is fix round 1's correction.
 *
 * The first build derived them from rows each tree happened to have loaded:
 * the SSR page counted the twenty listings it fetched for the cards, and the
 * SPA read `fetchAreaCounts()`, which counts `listings` alone AND silently
 * drops every neighbourhood whose listings carry no lat/lng — that fetch
 * exists to compute centroids for SS-2.3's nearby-areas state, not to count
 * anything. So the tiles said 18 / 8 / 6 against a catalogue of 1,642, and
 * they left out the 1,582 indexed rows entirely: 94% of what is actually for
 * sale in those areas, missing from the surface whose only job is to say how
 * much is for sale in them.
 *
 * Both trees now read `public.home_area_counts` — one view, both sources, the
 * feed's own visibility predicate. See the migration
 * 20260817100000_home01_area_counts.sql. The tiles are the top five BY THAT
 * COUNT, not a hardcoded list and not whichever five a query returned first.
 *
 * IMAGERY IS PER-AREA AND OPTIONAL (HOME-03). The design puts a photograph
 * behind each tile. Where Dirie has supplied one it renders; where he has not,
 * the tile falls back to a brand-tinted panel carrying the area's initial. That
 * fallback is not a placeholder waiting to be removed — the row is ranked by
 * LIVE counts over 231 neighbourhoods, so an area with no photograph can enter
 * the top five at any time and the tile has to be correct when it does.
 *
 * The two dishonest ways to fill a gap were refused and stay refused: a stock
 * photo we hold no licence to, and a listing's own photo standing in for the
 * area — which would put one agent's home under a neighbourhood's name without
 * them agreeing to it. See config/homeImages.ts for the URLs and the export
 * dimensions.
 *
 * A tile whose count is unknown renders WITHOUT the count line rather than with
 * a zero — the rule from the decision table, and the difference between "we did
 * not count" and "there are none".
 */

export interface HomeAreaTile {
  /** Neighbourhood name, exactly as `listings.neighborhood` stores it. */
  name: string;
  /** Published listings in this area. Null → the count line is omitted. */
  count: number | null;
  /** Reserved for real area photography. Null → the brand panel renders. */
  imageUrl?: string | null;
}

interface AreasRowProps {
  areas: HomeAreaTile[];
  viewAllHref?: string;
  /** Desktop shows five, mobile four. */
  limit?: number;
  /**
   * SPA only: apply the area filter in place instead of navigating.
   *
   * Home hydrates its filters from the URL ONCE, on mount (pages/Home.tsx), and
   * thereafter writes the URL with `history.replaceState`. So a router push at
   * `/?locations=Kilimani` while Home is already mounted changes the address bar
   * and nothing else. When this handler is supplied the tiles become buttons
   * that set the filter directly; without it they stay links, which is correct
   * for the server render and for any cold arrival at that URL.
   */
  onSelectArea?: (areaName: string) => void;
}

/**
 * A search for this area — `/?locations=<name>` is the URL `hydrateFiltersFromUrl`
 * already reads (lib/search/urlState.ts), so the tile lands on a real filtered
 * feed rather than an /areas/ route that does not exist.
 */
export function areaHref(name: string): string {
  return `/?locations=${encodeURIComponent(name)}`;
}

/** Deterministic brand panel — same area, same tint, every render and both trees. */
function panelStyle(name: string): React.CSSProperties {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) hash = (hash * 31 + name.charCodeAt(i)) % 360;
  const angle = 120 + (hash % 60);
  return {
    background: `linear-gradient(${angle}deg, #134025 0%, #1B5533 55%, #2A6B44 100%)`,
  };
}

const AreaVisual: React.FC<{ area: HomeAreaTile; className: string }> = ({ area, className }) =>
  area.imageUrl ? (
    <img
      src={getAreaTileUrl(area.imageUrl)}
      srcSet={getAreaTileSrcSet(area.imageUrl)}
      alt=""
      loading="lazy"
      decoding="async"
      /*
        The two-column tile EXACTLY: 18px page gutter each side, 12px grid gap,
        split in two. `50vw` was the earlier approximation and overstated the
        box by 24px, which is the safe direction for sharpness but pushed some
        widths a whole candidate higher than they needed — a 430 phone at 3×
        fetched 800w for a box that wanted 573.

        Below 1024 this expression is the mobile grid; at and above it the
        five-column desktop grid renders at 248 CSS px, capped by the 1440
        shell, so 250 holds at every width above the breakpoint.
      */
      sizes="(max-width: 1023px) calc((100vw - 48px) / 2), 250px"
      className={`w-full object-cover ${className}`}
    />
  ) : (
    <div
      className={`flex w-full items-center justify-center ${className}`}
      style={panelStyle(area.name)}
      aria-hidden="true"
    >
      <span className="text-[34px] font-extrabold text-white/25">{area.name.charAt(0)}</span>
    </div>
  );

/** "1 home", "312 homes". */
const homes = (count: number) => `${formatCount(count)} ${count === 1 ? 'home' : 'homes'}`;

const AreasRow: React.FC<AreasRowProps> = ({ areas, viewAllHref, limit = 5, onSelectArea }) => {
  const tiles = areas.slice(0, limit);
  if (tiles.length === 0) return null;

  /** A link on the server, a filter button once the SPA can apply one. */
  const Tile: React.FC<{ area: HomeAreaTile; className: string; children: React.ReactNode }> = ({
    area,
    className,
    children,
  }) =>
    onSelectArea ? (
      <button
        type="button"
        onClick={() => onSelectArea(area.name)}
        aria-label={
          area.count === null
            ? `Browse homes for sale in ${area.name}`
            : `Browse ${homes(area.count)} for sale in ${area.name}`
        }
        className={`${className} text-left ${FOCUS_RING}`}
      >
        {children}
      </button>
    ) : (
      <HomeLink to={areaHref(area.name)} className={`${className} ${FOCUS_RING}`}>
        {children}
      </HomeLink>
    );

  return (
    <section aria-labelledby="home-areas">
      <RowHeader title="Browse by area" viewAllHref={viewAllHref} className="mb-2.5 lg:mb-3.5" />

      {/* Mobile: 2-col overlay tiles. Desktop: 5-col cards with a caption below. */}
      <div className="grid grid-cols-2 gap-3 lg:hidden">
        {tiles.slice(0, 4).map((area) => (
          <Tile
            key={area.name}
            area={area}
            className="relative block h-[110px] w-full overflow-hidden rounded-xl"
          >
            <AreaVisual area={area} className="h-full" />
            <div
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  'linear-gradient(to top,rgba(10,20,12,.55),rgba(10,20,12,.05) 60%)',
              }}
            />
            <div
              className="pointer-events-none absolute bottom-2.5 left-3 text-white"
              style={{ textShadow: '0 1px 5px rgba(0,0,0,.5)' }}
            >
              <div className="text-[14px] font-extrabold">{area.name}</div>
              {area.count !== null && (
                <div className="text-[11px] font-semibold opacity-90">
                  {homes(area.count)}
                </div>
              )}
            </div>
          </Tile>
        ))}
      </div>

      <div className="hidden grid-cols-5 gap-4 lg:grid">
        {tiles.map((area) => (
          <Tile
            key={area.name}
            area={area}
            className={`block w-full overflow-hidden rounded-xl border border-[#E7E6E6] bg-white ${CARD_HOVER}`}
          >
            <AreaVisual area={area} className="h-[120px]" />
            <div className="px-3.5 pb-3 pt-2.5">
              <div className="text-[14.5px] font-bold text-[#2D2D2D]">{area.name}</div>
              {area.count !== null && (
                <div className="text-[12px] text-[#666666]">
                  {homes(area.count)} for sale
                </div>
              )}
            </div>
          </Tile>
        ))}
      </div>
    </section>
  );
};

export default AreasRow;
